/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

/**
 * Helper function to collapse/expand descendants of a node.
 * Traverses both outgoing and incoming links to reach all connected
 * agent/tool nodes (some agents only have outgoing edges back to a supervisor
 * without any incoming edge from the turn's forward path).
 */
export function setDescendantsVisibility(
  node: go.Node,
  visible: boolean,
): void {
  const visited = new Set<string>();

  function traverse(n: go.Node) {
    if (!n || visited.has(n.key as string)) return;
    visited.add(n.key as string);

    // Follow outgoing links
    n.findLinksOutOf().each((link) => {
      processLink(link, link.toNode);
    });

    // Follow incoming links to catch nodes only connected in reverse direction
    n.findLinksInto().each((link) => {
      processLink(link, link.fromNode);
    });
  }

  function processLink(link: go.Link, connectedNode: go.Node | null) {
    // Skip turn-to-turn sequence links - they should always be visible
    const isTurnSequenceLink = link.data?.edgeType === 'TURN_SEQUENCE';

    if (connectedNode && connectedNode.category !== 'end' &&
        connectedNode.category !== 'turn' && connectedNode.category !== 'start' &&
        connectedNode.category !== 'turn_loader' &&
        !isTurnSequenceLink) {
      // Skip 'end', 'turn', and 'start' nodes - they're part of the narrative layer.
      // Skip 'turn_loader' - the pagination placeholder is owned by React state
      // (visible while narrativeTurnsFetchingMore is true) and must survive
      // accordion collapses of the last turn that originated the fetch.
      // Skip TURN_SEQUENCE edges - they connect turns and should remain visible.
      link.visible = visible;
      connectedNode.visible = visible;
      if (visible) {
        // If expanding, check if connected node is also collapsed
        const targetExpanded = connectedNode.data?.isExpanded;
        if (targetExpanded === false) {
          // Don't expand descendants of collapsed nodes
          return;
        }
      }
      traverse(connectedNode);
    }
  }

  traverse(node);
}

/**
 * Reposition groups dynamically based on expanded turn nodes
 * Groups stack vertically at fixed X offset, but Y position based on visible turns only
 */
export function repositionGroups(diagram: go.Diagram): void {
  const TURN_X = -100;
  const GROUP_X_OFFSET = 250;
  const TURN_VERTICAL_SPACING = 150;
  const TURN_START_Y = 100;

  // Collect all turn nodes with their original vertical positions
  const turnNodes: Array<{ node: go.Node; originalY: number; index: number }> = [];

  diagram.nodes.each((node) => {
    if (!(node instanceof go.Group) && node.data.category === 'turn') {
      const y = node.location.y;
      const index = Math.round((y - TURN_START_Y) / TURN_VERTICAL_SPACING);
      turnNodes.push({ node, originalY: y, index });
    }
  });

  // Sort by index to maintain turn order
  turnNodes.sort((a, b) => a.index - b.index);

  // Position each expanded group at the same Y as its parent turn node
  // This ensures a fixed horizontal distance between the turn and its first agent/tool
  const groupPositions = new Map<string, go.Point>();

  turnNodes.forEach(({ node }) => {
    const isExpanded = node.data.isExpanded;
    const groupKey = `group_${node.key}`;
    const skeletonGroupKey = `skeleton_group_${node.key}`;

    if (isExpanded) {
      const pos = new go.Point(TURN_X + GROUP_X_OFFSET, node.location.y);
      // Align group Y with the turn node's Y so the distance is always consistent
      groupPositions.set(groupKey, pos);
      // Also reposition skeleton placeholder group (if present) to the same Y
      groupPositions.set(skeletonGroupKey, pos);
    }
  });

  // Apply new positions to groups via model data so the location binding picks them up
  groupPositions.forEach((position, groupKey) => {
    const group = diagram.findNodeForKey(groupKey);
    if (group && group instanceof go.Group) {
      diagram.model.setDataProperty(
        group.data,
        'location',
        go.Point.stringify(position),
      );
    }
  });
}
